import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Loader2, MapPin, Phone, Send } from "lucide-react";
import { Reveal } from "./Reveal";
import { API, BUSINESS, HOURS, ISSUES, getOpenStatus } from "./data";

const EMPTY = { name: "", phone: "", device: "", issue: ISSUES[0], message: "" };

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#07080c] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-blue-400/60";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const status = getOpenStatus();

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Anfrage gesendet!", {
        description: "Vielen Dank — wir melden uns schnellstmöglich bei Ihnen.",
      });
      setForm(EMPTY);
    } catch {
      toast.error("Senden fehlgeschlagen", {
        description: "Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative border-t border-white/10 bg-[#0d0f17] py-24 sm:py-32"
    >
      <div className="absolute -top-20 left-1/2 h-[360px] w-[560px] -translate-x-1/2 rounded-full bg-blue-500/10 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400">
            (04) — Kontakt
          </p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold text-slate-50 sm:text-5xl">
            Bereit für die <span className="text-outline">Rettung?</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-2">
            <Reveal>
              <div
                data-testid="storefront-photo-card"
                className="group relative overflow-hidden rounded-2xl border border-white/10"
              >
                <img
                  src="/ladenfoto.webp"
                  alt="Schaufenster von H.Elektronik am Westring 37 in Gütersloh"
                  loading="lazy"
                  className="aspect-[3/2] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07080c]/80 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
                  <span>Unser Laden</span>
                  <span className="text-blue-400">Westring 37</span>
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div
                data-testid="address-info-card"
                className="rounded-2xl border border-white/10 bg-[#07080c] p-7"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-blue-400/30 bg-blue-400/10">
                    <MapPin className="h-4 w-4 text-blue-400" />
                  </span>
                  <h3 className="font-display text-lg font-bold text-slate-50">Werkstatt</h3>
                </div>
                <p className="mt-4 text-base font-semibold text-slate-100">
                  {BUSINESS.street}
                </p>
                <p className="text-sm text-slate-400">
                  {BUSINESS.city} · {BUSINESS.area}
                </p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                  N 51.9069° · O 8.3785°
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <a
                href={BUSINESS.phoneHref}
                data-testid="contact-phone-card"
                className="group block rounded-2xl border border-white/10 bg-[#07080c] p-7 transition-colors duration-300 hover:border-blue-400/40"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-blue-400/30 bg-blue-400/10">
                    <Phone className="h-4 w-4 text-blue-400 transition-transform duration-300 group-hover:rotate-12" />
                  </span>
                  <h3 className="font-display text-lg font-bold text-slate-50">
                    Direkter Draht
                  </h3>
                </div>
                <p className="mt-4 text-base font-semibold text-slate-100 transition-colors duration-300 group-hover:text-blue-400">
                  Mobil {BUSINESS.phoneDisplay}
                </p>
                <p className="text-sm text-slate-400">
                  Anrufen genügt — Diagnose vor Ort kostenfrei.
                </p>
              </a>
            </Reveal>

            <Reveal delay={0.15}>
              <div
                data-testid="opening-hours-card"
                className="rounded-2xl border border-white/10 bg-[#07080c] p-7"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-blue-400/30 bg-blue-400/10">
                      <Clock className="h-4 w-4 text-blue-400" />
                    </span>
                    <h3 className="font-display text-lg font-bold text-slate-50">
                      Öffnungszeiten
                    </h3>
                  </div>
                  <span
                    data-testid="hours-live-status"
                    className={`flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
                      status.open
                        ? "border-emerald-400/30 text-emerald-400"
                        : "border-blue-400/30 text-blue-400"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full animate-pulse-dot ${
                        status.open ? "bg-emerald-400" : "bg-blue-400"
                      }`}
                    />
                    {status.open ? "Geöffnet" : "Geschlossen"}
                  </span>
                </div>
                <dl className="mt-5 space-y-3">
                  {HOURS.map((h) => (
                    <div
                      key={h.days}
                      className="flex items-center justify-between border-b border-white/5 pb-3 text-sm last:border-0 last:pb-0"
                    >
                      <dt className="text-slate-400">{h.days}</dt>
                      <dd
                        className={
                          h.time === "Geschlossen"
                            ? "font-mono text-xs uppercase tracking-wider text-slate-600"
                            : "font-semibold text-slate-100"
                        }
                      >
                        {h.time}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form
              onSubmit={submit}
              data-testid="repair-contact-form"
              className="glass-panel h-full rounded-2xl border border-white/10 p-7 sm:p-10"
            >
              <h3 className="font-display text-2xl font-bold text-slate-50">
                Reparatur-Anfrage
              </h3>
              <p className="mt-2 text-sm text-slate-400">
                Schildern Sie uns kurz Ihr Anliegen — wir melden uns mit Termin
                und Festpreis bei Ihnen.
              </p>

              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Ihr Name *
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Max Mustermann"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Telefonnummer *
                  </label>
                  <input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    required
                    minLength={5}
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="0151 23456789"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-device" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Gerät *
                  </label>
                  <input
                    id="contact-device"
                    data-testid="contact-device-input"
                    required
                    minLength={2}
                    value={form.device}
                    onChange={update("device")}
                    placeholder="z. B. iPhone 13"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-issue" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Anliegen *
                  </label>
                  <select
                    id="contact-issue"
                    data-testid="contact-issue-select"
                    value={form.issue}
                    onChange={update("issue")}
                    className={`${inputCls} appearance-none`}
                  >
                    {ISSUES.map((i) => (
                      <option key={i} value={i} className="bg-[#0d0f17]">
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Nachricht (optional)
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Beschreiben Sie kurz, was passiert ist …"
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-blue-400 px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#07080c] transition-all duration-300 hover:bg-slate-50 hover:shadow-[0_0_40px_rgba(59,130,246,0.4)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Wird gesendet …
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Anfrage senden
                  </>
                )}
              </button>
              <p className="mt-4 text-center text-xs text-slate-600">
                Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten zur
                Bearbeitung der Anfrage zu.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
